<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, minValue } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaignPlans/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxDefault: useMapGetter('inboxes/getWhatsAppInboxDefault'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
};

const initialState = {
  name: '',
  inboxId: null,
  templateId: null,
  selectedAudience: [],
  defaultVolume: 100,
  responseMessageText: null,
  followUpMessageText: null,
  followUpDelayMinutes: 60,
};

const state = reactive({ ...initialState });
const templateParserRef = ref(null);

const rules = {
  name: { required, minLength: minLength(1) },
  inboxId: { required },
  templateId: { required },
  selectedAudience: { required },
  defaultVolume: { required, minValue: minValue(1) },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

// Verificar se o template tem botões de resposta rápida (sim/não)
const hasQuickReplyButtons = computed(() => {
  if (!selectedTemplate.value) return false;
  const buttonComponent = selectedTemplate.value.components?.find(
    component => component.type === 'BUTTONS'
  );
  if (!buttonComponent?.buttons) return false;
  
  // Verificar se tem botões QUICK_REPLY
  const hasQuickReply = buttonComponent.buttons.some(
    button => button.type === 'QUICK_REPLY'
  );
  
  // Verificar se tem botão "Sim" ou "sim"
  const hasYesButton = buttonComponent.buttons.some(
    button => button.type === 'QUICK_REPLY' && 
    (button.text?.toLowerCase() === 'sim' || button.text?.toLowerCase() === 'yes' || button.text?.toLowerCase() === 'sí')
  );
  
  return hasQuickReply && hasYesButton;
});

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN_PLANS.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  name: getErrorMessage('name', 'NAME'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
  defaultVolume: getErrorMessage('defaultVolume', 'DEFAULT_VOLUME'),
}));

const hasRequiredTemplateParams = computed(() => {
  return !templateParserRef.value?.isFormInvalid;
});

const isSubmitDisabled = computed(
  () => v$.value.$invalid || !hasRequiredTemplateParams.value
);

const resetState = () => {
  Object.assign(state, initialState);
  if (formState.inboxDefault.value?.id) {
    state.inboxId = formState.inboxDefault.value.id;
  }
  v$.value.$reset();
};

const handleCancel = () => emit('cancel');

const prepareCampaignPlanDetails = () => {
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
    response_message_text: state.responseMessageText || null,
    follow_up_message_text: state.followUpMessageText || null,
    follow_up_delay_minutes: state.followUpDelayMinutes || null,
    rendered_template: parserData?.renderedTemplate || '',
  };

  return {
    campaign_plan: {
      name: state.name,
      inbox_id: state.inboxId,
      template_params: templateParams,
      default_volume: state.defaultVolume,
      audience: state.selectedAudience?.map(id => ({
        id,
        type: 'Label',
      })),
    },
  };
};

const handleSubmit = async () => {
  if (isSubmitDisabled.value) return;
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignPlanDetails());
  resetState();
  handleCancel();
};

// Initialize inbox with default WhatsApp inbox
if (formState.inboxDefault.value?.id) {
  state.inboxId = formState.inboxDefault.value.id;
}

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
    state.responseMessageText = null;
    state.followUpMessageText = null;
    state.followUpDelayMinutes = 60;
  }
);

// Reset response template when main template changes
watch(
  () => state.templateId,
  () => {
    if (!hasQuickReplyButtons.value) {
      state.responseMessageText = null;
      state.followUpMessageText = null;
      state.followUpDelayMinutes = 60;
    }
  }
);

// Reset follow-up when response message is cleared
watch(
  () => state.responseMessageText,
  (newValue) => {
    if (!newValue) {
      state.followUpMessageText = null;
      state.followUpDelayMinutes = 60;
    }
  }
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.name"
      :label="t('CAMPAIGN_PLANS.FORM.NAME.LABEL')"
      :placeholder="t('CAMPAIGN_PLANS.FORM.NAME.PLACEHOLDER')"
      :message="formErrors.name"
      :message-type="formErrors.name ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="template" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN_PLANS.FORM.TEMPLATE.LABEL') }}
      </label>
      <ComboBox
        id="template"
        v-model="state.templateId"
        :options="templateOptions"
        :has-error="!!formErrors.template"
        :placeholder="t('CAMPAIGN_PLANS.FORM.TEMPLATE.PLACEHOLDER')"
        :message="formErrors.template"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <!-- Template Parser -->
    <WhatsAppTemplateParser
      v-if="selectedTemplate"
      ref="templateParserRef"
      :template="selectedTemplate"
    />

    <!-- Mensagem de Resposta (apenas se template principal tiver botões) -->
    <div v-if="hasQuickReplyButtons" class="flex flex-col gap-1">
      <TextArea
        id="responseMessageText"
        v-model="state.responseMessageText"
        :label="t('CAMPAIGN_PLANS.FORM.RESPONSE_MESSAGE.LABEL')"
        :placeholder="t('CAMPAIGN_PLANS.FORM.RESPONSE_MESSAGE.PLACEHOLDER')"
        auto-height
        min-height="8rem"
        max-height="20rem"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN_PLANS.FORM.RESPONSE_MESSAGE.INFO') }}
      </p>
    </div>

    <!-- Mensagem de Follow-up (apenas se Response Message estiver preenchida) -->
    <div v-if="hasQuickReplyButtons && state.responseMessageText" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <TextArea
          id="followUpMessageText"
          v-model="state.followUpMessageText"
          :label="t('CAMPAIGN_PLANS.FORM.FOLLOW_UP_MESSAGE.LABEL')"
          :placeholder="t('CAMPAIGN_PLANS.FORM.FOLLOW_UP_MESSAGE.PLACEHOLDER')"
          auto-height
          min-height="6rem"
          max-height="15rem"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN_PLANS.FORM.FOLLOW_UP_MESSAGE.INFO') }}
        </p>
      </div>

      <!-- Campo de tempo de espera (apenas se follow-up message estiver preenchida) -->
      <div v-if="state.followUpMessageText" class="flex flex-col gap-1">
        <Input
          id="followUpDelayMinutes"
          v-model.number="state.followUpDelayMinutes"
          type="number"
          min="1"
          max="1440"
          :label="t('CAMPAIGN_PLANS.FORM.FOLLOW_UP_DELAY.LABEL')"
          :placeholder="t('CAMPAIGN_PLANS.FORM.FOLLOW_UP_DELAY.PLACEHOLDER')"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN_PLANS.FORM.FOLLOW_UP_DELAY.INFO') }}
        </p>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN_PLANS.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN_PLANS.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN_PLANS.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model.number="state.defaultVolume"
      :label="t('CAMPAIGN_PLANS.FORM.DEFAULT_VOLUME.LABEL')"
      type="number"
      min="1"
      :placeholder="t('CAMPAIGN_PLANS.FORM.DEFAULT_VOLUME.PLACEHOLDER')"
      :message="formErrors.defaultVolume"
      :message-type="formErrors.defaultVolume ? 'error' : 'info'"
    />

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN_PLANS.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-text hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN_PLANS.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>

